import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { brand, footer } from "@/lib/content";

/**
 * LegalPage – shared layout for Villkor / Integritetspolicy / Cookies.
 * Takes a title + an array of sections ({heading, body}) rendered as
 * a clean, readable single-column article.
 */
export default function LegalPage({ eyebrow, title, updated, sections = [] }) {
  return (
    <div className="min-h-screen bg-slate-50 font-plex text-slate-900">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-outfit text-lg tracking-tight" data-testid="legal-brand">
            Redoc <span className="text-blue-600">AML &amp; KYC</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Till startsidan
          </Link>
        </div>
      </header>

      {/* Article */}
      <main className="max-w-3xl mx-auto px-6 pt-14 pb-24">
        {eyebrow && (
          <p className="text-xs uppercase tracking-[0.18em] text-blue-700 mb-3">{eyebrow}</p>
        )}
        <h1 className="font-outfit text-4xl sm:text-5xl tracking-tight text-slate-900">
          {title}
        </h1>
        {updated && (
          <p className="mt-3 text-sm text-slate-500">Senast uppdaterad: {updated}</p>
        )}

        <div className="mt-10 space-y-10">
          {sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              {s.heading && (
                <h2 className="font-outfit text-2xl text-slate-900 mb-3">
                  {s.heading}
                </h2>
              )}
              {Array.isArray(s.body)
                ? s.body.map((p, j) => (
                    <p
                      key={j}
                      className="text-base text-slate-900 leading-relaxed mb-3 whitespace-pre-line"
                    >
                      {p}
                    </p>
                  ))
                : (
                  <p className="text-base text-slate-900 leading-relaxed whitespace-pre-line">
                    {s.body}
                  </p>
                )}
              {s.list && (
                <ul className="mt-2 list-disc pl-6 space-y-1.5 text-base text-slate-900">
                  {s.list.map((li, k) => (
                    <li key={k}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-3 items-center text-xs text-slate-500">
          <div className="text-left">{footer.copy}</div>
          <div className="text-center">
            Web by{" "}
            <a
              href="https://logartgroup.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-300 hover:text-blue-400 transition-colors duration-200"
            >
              Logardt Group
            </a>
          </div>
          <div className="text-right">
            <Link to="/" className="hover:text-white">{brand.name}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
